import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { toast } from "sonner";
import api, { formatApiErrorDetail, getToken } from "@/lib/api";
import { Gavel, Loader2, Sparkles, Save, FileText, Send, Plus, Trash2 } from "lucide-react";

const NUM_FIELDS = [
  ["demand_amount", "Settlement demand ($)"], ["policy_limits", "Policy limits ($)"],
  ["lost_wages", "Lost wages ($)"], ["other_damages", "Other damages ($)"],
];
const TXT_FIELDS = [
  ["insurer_name", "Insurer"], ["adjuster_name", "Adjuster"],
  ["claim_number", "Claim number"], ["date_of_loss", "Date of loss"],
];

export default function DemandBuilder() {
  const { caseId } = useParams();
  const [dp, setDp] = useState(null);
  const [avail, setAvail] = useState([]);
  const [busy, setBusy] = useState("");

  const load = async () => {
    const { data } = await api.get(`/cases/${caseId}/demand`);
    setAvail(data.available_exhibits || []);
    setDp({ medical_specials: [], exhibits: [], ...data });
  };
  useEffect(() => { load().catch(() => {}); /* eslint-disable-next-line */ }, [caseId]);
  if (!dp) return <div className="mt-8 flex justify-center py-8"><Loader2 className="w-5 h-5 text-cyan-400 animate-spin" /></div>;

  const set = (k, v) => setDp({ ...dp, [k]: v });
  const setSpec = (i, k, v) => { const m = [...(dp.medical_specials || [])]; m[i] = { ...m[i], [k]: v }; set("medical_specials", m); };
  const addSpec = () => set("medical_specials", [...(dp.medical_specials || []), { provider: "", description: "", date: "", amount: "" }]);
  const rmSpec = (i) => set("medical_specials", dp.medical_specials.filter((_, x) => x !== i));
  const total = (dp.medical_specials || []).reduce((s, r) => s + (Number(r.amount) || 0), 0) + (Number(dp.lost_wages) || 0) + (Number(dp.other_damages) || 0);

  const payload = () => ({
    demand_amount: dp.demand_amount ? Number(dp.demand_amount) : null,
    policy_limits: dp.policy_limits ? Number(dp.policy_limits) : null,
    lost_wages: dp.lost_wages ? Number(dp.lost_wages) : null,
    other_damages: dp.other_damages ? Number(dp.other_damages) : null,
    insurer_name: dp.insurer_name, adjuster_name: dp.adjuster_name, claim_number: dp.claim_number,
    date_of_loss: dp.date_of_loss,
    medical_specials: (dp.medical_specials || []).map((m) => ({ ...m, amount: Number(m.amount) || 0 })),
    letter_html: dp.letter_html, liability_summary_html: dp.liability_summary_html,
    exhibits: dp.exhibits || [],
  });

  const save = async () => { setBusy("save"); try { await api.put(`/cases/${caseId}/demand`, payload()); toast.success("Demand package saved"); } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(""); } };
  const generate = async () => {
    setBusy("gen");
    try { const { data } = await api.post(`/cases/${caseId}/demand/generate`, payload()); setDp({ ...dp, ...data }); toast.success("AI draft generated — review & edit"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(""); }
  };
  const prepare = async () => {
    setBusy("prep");
    try { await api.put(`/cases/${caseId}/demand`, payload()); await api.post("/communications", { case_id: caseId, type: "demand_package" }); toast.success("Draft created in Firm → Delivery Center"); }
    catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail)); } finally { setBusy(""); }
  };
  const pdfUrl = `${process.env.REACT_APP_BACKEND_URL}/api/cases/${caseId}/demand/pdf?auth=${getToken()}`;
  const toggleEx = (ex) => {
    const has = (dp.exhibits || []).some((e) => e.label === ex.label);
    set("exhibits", has ? dp.exhibits.filter((e) => e.label !== ex.label) : [...(dp.exhibits || []), ex]);
  };

  const input = "bg-slate-900 border border-slate-700 rounded-lg px-3 py-2 text-sm text-white outline-none focus:border-cyan-500";

  return (
    <div className="mt-8 rounded-3xl border border-slate-800 bg-navy text-slate-100 p-6" data-testid="demand-builder">
      <p className="text-xs font-medium text-cyan-400 uppercase tracking-widest flex items-center gap-1.5"><Gavel className="w-3.5 h-3.5" /> Demand Package Builder</p>
      <h2 className="font-heading text-2xl font-extrabold text-white mt-1">Settlement Demand</h2>
      <p className="text-xs text-slate-500 mt-1">AI assembles a draft from case facts — you review, edit, and approve. Delivered on your firm's letterhead through the Delivery Center.</p>

      <div className="mt-4 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
        {TXT_FIELDS.map(([k, l]) => (
          <label key={k} className="block"><span className="text-[11px] text-slate-400">{l}</span>
            <input value={dp[k] || ""} onChange={(e) => set(k, e.target.value)} data-testid={`dmd-${k}`} className={`mt-1 w-full ${input}`} /></label>
        ))}
        {NUM_FIELDS.map(([k, l]) => (
          <label key={k} className="block"><span className="text-[11px] text-slate-400">{l}</span>
            <input type="number" value={dp[k] ?? ""} onChange={(e) => set(k, e.target.value)} data-testid={`dmd-${k}`} className={`mt-1 w-full ${input}`} /></label>
        ))}
      </div>

      <div className="mt-5">
        <div className="flex items-center justify-between">
          <h3 className="font-semibold text-white text-sm">Medical Specials & Damages</h3>
          <button onClick={addSpec} data-testid="dmd-add-special" className="flex items-center gap-1 text-xs text-cyan-300 hover:text-cyan-200"><Plus className="w-3.5 h-3.5" /> Add line</button>
        </div>
        <div className="mt-2 space-y-2">
          {(dp.medical_specials || []).map((row, i) => (
            <div key={i} className="grid grid-cols-[1fr,1fr,120px,110px,32px] gap-2 items-center" data-testid={`dmd-special-${i}`}>
              <input value={row.provider || ""} onChange={(e) => setSpec(i, "provider", e.target.value)} placeholder="Provider" className={input} />
              <input value={row.description || ""} onChange={(e) => setSpec(i, "description", e.target.value)} placeholder="Description" className={input} />
              <input value={row.date || ""} onChange={(e) => setSpec(i, "date", e.target.value)} placeholder="Date" className={input} />
              <input type="number" value={row.amount ?? ""} onChange={(e) => setSpec(i, "amount", e.target.value)} placeholder="Amount" className={input} />
              <button onClick={() => rmSpec(i)} className="p-1.5 text-slate-500 hover:text-red-300"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          {(dp.medical_specials || []).length === 0 && <p className="text-xs text-slate-500">No line items yet.</p>}
        </div>
        <div className="mt-2 text-right text-sm text-cyan-300 font-semibold" data-testid="dmd-total">Total documented damages: ${total.toLocaleString()}</div>
      </div>

      {avail.length > 0 && (
        <div className="mt-4">
          <h3 className="font-semibold text-white text-sm mb-2">Evidence Exhibits</h3>
          <div className="flex flex-wrap gap-2">
            {avail.map((ex) => {
              const on = (dp.exhibits || []).some((e) => e.label === ex.label);
              return <button key={ex.label} onClick={() => toggleEx(ex)} data-testid={`dmd-exhibit-${ex.label}`}
                className={`text-xs px-3 py-1.5 rounded-full border ${on ? "bg-cyan-500 text-slate-950 border-cyan-500" : "bg-slate-900 text-slate-300 border-slate-700"}`}>{ex.label}</button>;
            })}
          </div>
        </div>
      )}

      <div className="mt-5 flex flex-wrap gap-2">
        <button onClick={generate} disabled={busy === "gen"} data-testid="dmd-generate" className="flex items-center gap-2 px-5 py-2 rounded-full bg-cyan-500 text-slate-950 text-sm font-semibold hover:bg-cyan-400 disabled:opacity-50">{busy === "gen" ? <Loader2 className="w-4 h-4 animate-spin" /> : <Sparkles className="w-4 h-4" />} Generate with AI</button>
        <button onClick={save} disabled={busy === "save"} data-testid="dmd-save" className="flex items-center gap-2 px-5 py-2 rounded-full bg-slate-800 text-slate-200 text-sm hover:bg-slate-700 disabled:opacity-50"><Save className="w-4 h-4" /> Save</button>
        <a href={pdfUrl} target="_blank" rel="noreferrer" data-testid="dmd-pdf" className="flex items-center gap-2 px-5 py-2 rounded-full bg-slate-800 text-cyan-300 text-sm hover:bg-slate-700"><FileText className="w-4 h-4" /> Preview PDF</a>
        <button onClick={prepare} disabled={busy === "prep"} data-testid="dmd-prepare" className="flex items-center gap-2 px-5 py-2 rounded-full bg-emerald-500 text-slate-950 text-sm font-semibold hover:bg-emerald-400 disabled:opacity-50"><Send className="w-4 h-4" /> Prepare for Delivery</button>
      </div>

      <div className="mt-5 grid lg:grid-cols-2 gap-4">
        <label className="block"><span className="text-[11px] text-slate-400">Demand letter (HTML — editable)</span>
          <textarea value={dp.letter_html || ""} onChange={(e) => set("letter_html", e.target.value)} rows={10} data-testid="dmd-letter" className={`mt-1 w-full ${input} font-mono text-xs`} /></label>
        <label className="block"><span className="text-[11px] text-slate-400">Liability summary (HTML — editable)</span>
          <textarea value={dp.liability_summary_html || ""} onChange={(e) => set("liability_summary_html", e.target.value)} rows={10} data-testid="dmd-liability" className={`mt-1 w-full ${input} font-mono text-xs`} /></label>
      </div>
    </div>
  );
}
